import {
  Button,
  Card,
  Container,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import pluralize from "pluralize";
import SocialShare from "../../common/SocialShare";
import {
  useIsBoardAdmin,
  useIsBoardReferee,
} from "../../common/boards/board.hooks";
import { Board } from "../../common/boards/board.schema";
import {
  EventType,
  // SeedType,
  Event,
  TournamentType,
} from "../../common/events/event.schema";
import { openConfirmModal } from "@mantine/modals";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { completeEvent } from "../../common/api";
import { Member } from "../../common/members/member.schema";
import MemberTable from "../members/MemberTable";
import PrintSeedTable from "./PrintSeedTable";
import SeedTable from "./SeedTable";
import {
  getEventName,
  getHasEliminations,
  getHasPools,
  getHasTeams,
  getIsEndless,
} from "./util";
import USFAEventRating from "./USFAEventRating";
import LiveMatches from "../matches/LiveMatches";

const SummaryInProgress = ({
  event,
  board,
  onSelectViewCurrent,
}: {
  event: Event;
  board: Board;
  onSelectViewCurrent?: () => void;
}) => {
  const hasTeams = getHasTeams(event);
  const hasPools = getHasPools(event);
  const isRef = useIsBoardReferee();
  const hasEliminations = getHasEliminations(event);
  const isEndless = getIsEndless(event);
  const poolPlural =
    event.pools.length > 0 ? pluralize("pool", event.pools.length) : null;
  let activeCount = event.members.length;
  if (event.type === TournamentType.TEAM_POOLS_ELIMINATION)
    activeCount = event.teams.length;
  if (!event.started && event.registrations)
    activeCount += event.registrations?.filter((r) =>
      event.requireCheckIn ? r.approved && r.checkedIn : r.approved
    ).length;
  // const roundCount = event.eliminationRounds;
  let roundCount = event.eliminations.length;
  // console.log("event.eliminations :", event.eliminations);
  // console.log("roundCount :", roundCount);
  // console.log("event.eliminationRounds :", event.eliminationRounds);
  // const queryClient = useQueryClient();
  let elimMemberCount = event.members.length;
  if (hasTeams) {
    elimMemberCount = event.teams.length;
  }
  if (event.type === TournamentType.TEAM_POOLS_ELIMINATION_SPLIT) {
    elimMemberCount = Math.floor(event.members.length / 3);
  }
  // console.log('elimMemberCount :', elimMemberCount);
  if (roundCount === 0) {
    while (roundCount < event.eliminationRounds) {
      roundCount++;
      // console.log("roundCount :", roundCount);
      const members = Math.pow(2, roundCount);

      // console.log("members :", members);
      if (members >= elimMemberCount) break;
    }
  }
  const queryClient = useQueryClient();
  const { mutate: doComplete, isLoading } = useMutation(completeEvent, {
    onSuccess: () => {
      queryClient.invalidateQueries(["event", event.shortLink]);
    },
  });
  const roundPlural = pluralize("round", roundCount);
  const elimMemberName = hasTeams ? "teams" : "members";
  const isTournament = event.eventType === EventType.TOURNAMENT;
  const isAdmin = useIsBoardAdmin();
  // const { mutate } = useMutation(updateEvent, {
  //   onSuccess: () => {
  //     queryClient.invalidateQueries(["event", event.shortLink]);
  //   },
  // });
  // function onReorder(members: string[]): void {
  //   mutate({ _id: event._id, seeds: members });
  // }
  // let seedsSortable = isRef && event.seedType === SeedType.MANUAL;
  // if (seedsSortable) seedsSortable = !(event.matches.length > 0);

  const onClickComplete = () => {
    openConfirmModal({
      title: "Complete event",
      children: (
        <Text size="sm">Are you sure you want to complete this event?</Text>
      ),
      labels: { confirm: "Complete", cancel: "Cancel" },
      onConfirm: async () => {
        doComplete(event._id);
      },
    });
  };
  const canComplete = isAdmin && !event.complete;
  const showingPoolAllocations =
    hasPools && !event.poolsComplete && event.pools.length > 1;
  return (
    <Container size="sm" pt={10} px={0}>
      <Stack>
        <Title order={4}>
          {getEventName(event)} in progress <Loader size="xs" type="dots" />
        </Title>
        {hasPools && hasEliminations && !hasTeams && (
          <Text>
            {activeCount} {pluralize("member", activeCount)} in{" "}
            {event.pools.length} {poolPlural}, followed by {roundCount}{" "}
            elimination {roundPlural}
          </Text>
        )}
        {hasPools &&
          hasEliminations &&
          hasTeams &&
          event.type === TournamentType.TEAM_POOLS_ELIMINATION && (
            <Text>
              {activeCount} {pluralize("teams", activeCount)} in{" "}
              {event.pools.length} {poolPlural}, followed by {roundCount}{" "}
              elimination {roundPlural}
            </Text>
          )}
        {hasPools &&
          hasEliminations &&
          hasTeams &&
          event.type === TournamentType.TEAM_POOLS_ELIMINATION_SPLIT && (
            <Text>
              {activeCount} {pluralize("member", activeCount)} in{" "}
              {event.pools.length} {poolPlural}, to determine teams, followed by{" "}
              {roundCount} elimination {roundPlural}
            </Text>
          )}
        {hasPools && !hasEliminations && (
          <Text>
            {activeCount} {pluralize("member", activeCount)} in{" "}
            {event.pools.length} {isEndless ? "endless" : ""} {poolPlural},
          </Text>
        )}
        {!hasPools && hasEliminations && (
          <Text>
            {activeCount} {elimMemberName} in {roundCount} elimination{" "}
            {roundPlural}
          </Text>
        )}
        <USFAEventRating event={event} />
        {isTournament && (
          <>
            {event.started &&
              !event.complete &&
              hasPools &&
              !event.poolsComplete && (
                <Button
                  onClick={() => onSelectViewCurrent && onSelectViewCurrent()}
                >
                  go to the pool stage
                </Button>
              )}
            {event.started &&
              !event.complete &&
              (!hasPools || event.poolsComplete) && (
                <Button
                  onClick={() => onSelectViewCurrent && onSelectViewCurrent()}
                >
                  go to the elimination stage
                </Button>
              )}
            <Stack>
              <LiveMatches event={event} board={board} />
              {showingPoolAllocations && (
                <Title size="sm" order={3}>
                  Seeding and pool allocations
                </Title>
              )}
              {!showingPoolAllocations && (
                <Title size="sm" order={3}>
                  Seeding
                </Title>
              )}
              <Card>
                <SeedTable event={event} link editable={isRef} />
              </Card>
              {isRef && (
                <Group justify="right">
                  <PrintSeedTable event={event} board={board} />
                </Group>
              )}
            </Stack>
            <Card>
              <Stack>
                <Title order={3} size="xs">
                  Share live results
                </Title>
                <SocialShare board={board} event={event} />
              </Stack>
            </Card>
          </>
        )}
        {!isTournament && event.members.length > 0 && (
          <MemberTable members={event.members as Member[]} link />
        )}
        {canComplete && (
          <Button
            loading={isLoading}
            disabled={isLoading}
            onClick={onClickComplete}
          >
            Complete event
          </Button>
        )}
      </Stack>
    </Container>
  );
};
export default SummaryInProgress;
